import { useState, useEffect } from "react";
import type { ColumnsType } from "antd/es/table";
import TableList from "../../ui-component/Table";
import { Header } from "../../components/Header";
import { useGetData } from "../../Network/useGetQueryData";
import { useLocation, useNavigate } from "react-router-dom";
import { uniqBy } from "lodash";
import { Dropdown, MenuProps } from "antd";
import { DownOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { UserRolesId } from "../enum";

interface Providers {
  id?: number;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: null;
  logTracker?: string;
  name?: { en: string; ar?: string };
  emails?: string[];
  phoneNumbers?: string[];
  branches?: any[];
  cr?: string;
  taxNumber?: string;
  type?: string;
  status?: string;
  logoUrl?: string;
  websiteUrl?: string;
  contactPerson?: {
    name?: string;
    email?: string;
    countryCode?: string;
    phoneNumber?: string;
  };
}

const ProvidersList = () => {
  const userInfo = useSelector((state: any) => state.user.user);
  const { t, i18n } = useTranslation();
  const [offset, setOffset] = useState(0);
  const [searchKey, setSearchKey] = useState<string>();
  const [activeFilter, setActiveFilter] = useState<string>("active");
  const [providers, setProviders] = useState<Providers[]>([]);
  const [limit] = useState(10);
  const navigate = useNavigate();
  const location = useLocation();

  const providerColumns: ColumnsType<Providers> = [
    {
      title: t("providers.table.provider"),
      dataIndex: "name",
      key: "name",
      width: 300,
      render: (_, record) => (
        <div
          className="title-with-img"
          onClick={() =>
            userInfo.roleId === UserRolesId.Admin &&
            navigate(`/add-provider/${record?.id}`)
          }
        >
          <div
            className={`img-container ${record.status === "active" ? "active" : ""}`}
          >
            <span>
              <img
                src={record?.logoUrl || "/assets/providers/mokhtabar.png"}
                alt={record.name?.[i18n.language || "en"]}
                onError={(e) => {
                  (e.target as HTMLImageElement).src =
                    "/assets/providers/mokhtabar.png";
                }}
              />
            </span>
          </div>
          <p>{record.name?.[i18n.language || "en"]}</p>
        </div>
      ),
    },
    {
      title: t("providers.table.contactPerson"),
      dataIndex: "contactPerson",
      key: "contactPerson",
      render: (_, record) => <p>{record.contactPerson?.name}</p>,
    },
    {
      title: t("providers.table.contactEmail"),
      dataIndex: "id",
      key: "contactEmail",
      render: (_, record) => <p>{record.contactPerson?.email}</p>,
      width: 200,
    },
    {
      title: t("providers.table.contactPhone"),
      dataIndex: "cr",
      key: "contactPhone",
      render: (_, record) => <p>{record.contactPerson?.phoneNumber}</p>,
    },
    {
      title: t("providers.table.status"),
      dataIndex: "status",
      key: "status",
      render: (_, record) => (
        <p style={{ textTransform: "capitalize" }}>{record.status}</p>
      ),
    },
    {
      title: t("providers.table.branches"),
      dataIndex: "branches",
      key: "branches",
      render: (_, record) => (
        <p style={{ textTransform: "capitalize" }}>{record.branches?.length}</p>
      ),
    },
  ];

  const { data, isLoading } = useGetData<{
    data: { items: Providers[]; count: number };
  }>({
    queryKey: ["providers", { offset, limit, searchKey, location }],
    url: "/service-provider",
    params: { offset, limit, searchKey, withDeleted: false },
  });

  useEffect(() => {
    if (data?.data?.items && !!searchKey) {
      setProviders(data.data.items);
      return;
    }

    if (data?.data?.items) {
      setProviders((prevProviders) => [...prevProviders, ...data.data.items]);
    }
  }, [data]);

  const loadMoreItems = () => {
    if (data && providers.length < data.data.count && !isLoading) {
      setOffset((prevOffset) => prevOffset + 1);
    }
  };

  const menuItems: MenuProps["items"] = [
    {
      label: t("providers.filter.active"),
      key: "3",
      onClick: () => setActiveFilter("active"),
    },
    {
      type: "divider",
    },
    {
      label: t("providers.filter.inactive"),
      onClick: () => setActiveFilter("not_active"),
      key: "2",
    },
    {
      label: t("providers.filter.all"),
      onClick: () => setActiveFilter(undefined),
      key: "1",
    },
  ];

  const providerList = !!!activeFilter
    ? providers
    : providers?.filter((i) => i.status === activeFilter);

  return (
    <>
      <Header
        title={t("providers.header.title")}
        headerAction={
          userInfo?.roleId === UserRolesId.Admin
            ? {
                actionTitle: t("providers.header.add"),
                onAction: () => navigate("/add-provider"),
              }
            : null
        }
        headerFilter={
          <Dropdown
            menu={{
              items: menuItems,
            }}
            trigger={["click"]}
          >
            <span style={{ cursor: "pointer", margin: "0 20px" }}>
              <p
                className="status-header-text"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  color: "rgb(var(--primary-color))",
                  fontWeight: 600,
                }}
              >
                {activeFilter === "active"
                  ? t("providers.filter.active")
                  : activeFilter === "not_active"
                    ? t("providers.filter.inactive")
                    : t("providers.filter.all")}

                <DownOutlined />
              </p>
            </span>
          </Dropdown>
        }
        search={{
          placeholder: t("providers.search.placeholder"),
          searchAction: (val) => {
            offset > 0 && setOffset(0);
            setTimeout(() => {
              setSearchKey(val);
            }, 300);
          },
        }}
      />
      <TableList
        data={uniqBy(providerList, "id")}
        columns={providerColumns}
        loading={searchKey && isLoading}
        rowKey="id"
        loadMore={loadMoreItems}
        isLoadMore={isLoading}
      />
    </>
  );
};

export default ProvidersList;
